// composables/useUploadApi.ts - 文件上传 API 封装（两段式直传 + 检测）
// 阶段说明：
// - 第2阶段：占位返回模拟数据
// - 第3阶段：接入真实后端：申请直传 → XHR直传 → 完成上报 → 申请检测 → 轮询检测

import CryptoJS from 'crypto-js'
import type { ApiResponse } from '~/types/api'
import type { Int64Id } from '~/types/translation'
import { normalizeInt64IdOrThrow } from '~/utils/int64Id.js'

export interface UploadResult {
  fileId: Int64Id
  pages?: number
  fileName: string
  mimeType?: string
  isScanned?: boolean
}

export interface UploadOptions {
  signal?: AbortSignal
  onProgress?: (percent: number) => void
}

interface UploadApplyResult {
  fileId: Int64Id
  method: string
  url: string
  headers: Array<{ key: string; value: string }>
}

interface DetectResult {
  isEnd: boolean
  pollIntervalMs: number
  detectStatus: number
  pageCount?: number
  failedReason?: string
  isScanned?: boolean
}

export function useUploadApi() {
  const api = useNuxtApp().$api as <T = any>(
    path: string,
    opts?: { method?: string; body?: any }
  ) => Promise<ApiResponse<T>>

  // 计算文件MD5（增量）
  const md5File = async (file: File): Promise<string> => {
    const chunkSize = 2 * 1024 * 1024 // 2MB
    const chunks = Math.ceil(file.size / chunkSize)
    const md5 = CryptoJS.algo.MD5.create()

    let currentChunk = 0

    while (currentChunk < chunks) {
      const start = currentChunk * chunkSize
      const end = Math.min(file.size, start + chunkSize)
      const blobSlice = file.slice(start, end)
      const buffer = await blobSlice.arrayBuffer()
      const wordArray = CryptoJS.lib.WordArray.create(new Uint8Array(buffer))
      md5.update(wordArray)
      currentChunk++
      // 可选：此处可上报MD5进度
    }

    return md5.finalize().toString(CryptoJS.enc.Hex)
  }

  // 申请上传
  const applyUpload = async (file: File, fileMd5: string): Promise<UploadApplyResult> => {
    const resp = await api('/source_file_upload/apply_upload', {
      method: 'POST',
      body: {
        function: 'doc_translate',
        file_name: file.name,
        file_md5: fileMd5
      }
    })
    const d: any = (resp as any)?.data ?? resp
    const headers = (d.headers || []).map((h: any) => ({ key: h.key, value: h.value }))
    return {
      fileId: normalizeInt64IdOrThrow(d.file_id, 'file_id'),
      method: d.upload_http_method || 'PUT',
      url: d.upload_url,
      headers
    }
  }

  // 使用XHR直传，支持进度与取消
  const directUpload = (url: string, method: string, headers: Array<{ key: string; value: string }>, file: File, opts: UploadOptions = {}) => {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open(method || 'PUT', url)

      // 设置服务端返回的header
      for (const h of headers || []) {
        try {
          if (h?.key) xhr.setRequestHeader(h.key, h.value ?? '')
        } catch (err) {
          // 最小化吞错：受限头报错不阻断，记录调试日志
          // eslint-disable-next-line no-console
          console.debug('[upload] setRequestHeader failed:', h?.key, err)
        }
      }
      // 默认内容类型（若后端未要求其它）
      try {
        if (!(headers || []).some(h => h.key.toLowerCase() === 'content-type')) {
          xhr.setRequestHeader('Content-Type', 'application/octet-stream')
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.debug('[upload] set default Content-Type failed', err)
      }

      // 进度回调
      if (opts.onProgress) {
        xhr.upload.onprogress = (e) => {
          if (!e.lengthComputable) return
          const percent = Math.floor((e.loaded / e.total) * 100)
          opts.onProgress!(Math.max(0, Math.min(100, percent)))
        }
      }

      // 取消
      const onAbort = () => {
        try { xhr.abort() } catch (err) {
          // eslint-disable-next-line no-console
          console.debug('[upload] xhr.abort failed', err)
        }
      }
      if (opts.signal) {
        if (opts.signal.aborted) return reject(new DOMException('Aborted', 'AbortError'))
        const abortHandler = () => onAbort()
        opts.signal.addEventListener('abort', abortHandler, { once: true })
        // 在完成时移除监听
        const cleanup = () => opts.signal?.removeEventListener('abort', abortHandler)
        xhr.addEventListener('loadend', cleanup, { once: true })
      }

      xhr.onerror = () => reject(new Error('网络错误导致上传失败'))
      xhr.ontimeout = () => reject(new Error('上传超时'))
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // 确保进度条到 100
          if (opts.onProgress) opts.onProgress(100)
          resolve()
        } else {
          reject(new Error(`上传失败（HTTP ${xhr.status}）`))
        }
      }

      xhr.send(file)
    })
  }

  // 上报上传完成
  const reportUploadFinish = async (fileId: Int64Id, isSuccess: boolean) => {
    const normalizedFileId = normalizeInt64IdOrThrow(fileId, 'file_id')
    await api('/source_file_upload/upload_finish', {
      method: 'POST',
      body: { file_id: normalizedFileId, is_success: isSuccess }
    })
  }

  // 申请检测
  const applyDetect = async (fileId: Int64Id) => {
    const normalizedFileId = normalizeInt64IdOrThrow(fileId, 'file_id')
    await api('/source_file_upload/apply_detect_file', {
      method: 'POST',
      body: { file_id: normalizedFileId }
    })
  }

  // 轮询检测结果
  const pollDetect = async (fileId: Int64Id): Promise<DetectResult> => {
    const normalizedFileId = normalizeInt64IdOrThrow(fileId, 'file_id')
    // 立即查询一次，然后按返回间隔轮询，直到 is_end
    // 返回最终 DetectResult
    // 若失败抛出错误由上层处理（全局拦截器会提示）
    // 这里采用循环 + 延时而非 setTimeout，便于串行控制
    // 默认安全下限间隔 500ms
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const resp = await api('/source_file_upload/query_detect_file_result', {
        method: 'POST',
        body: { file_id: normalizedFileId }
      })
      const d: any = (resp as any)?.data ?? resp
      const result: DetectResult = {
        isEnd: !!d.is_end,
        pollIntervalMs: d.poll_interval_ms || 500,
        detectStatus: d.detect_status,
        pageCount: d.page_count ?? d.PageCount, // 兼容可能的大小写
        failedReason: d.failed_reason,
        isScanned: d.is_scanned_pdf === true
      }
      if (result.isEnd) return result
      const delay = Math.max(200, result.pollIntervalMs || 0)
      await new Promise(r => setTimeout(r, delay))
    }
  }

  // 上传文件（真实实现）
  const uploadFile = async (file: File, opts: UploadOptions = {}): Promise<UploadResult> => {
    // 1) 计算MD5
    const md5 = await md5File(file)

    // 2) 申请上传
    const apply = await applyUpload(file, md5)

    try {
      // 3) 直传
      await directUpload(apply.url, apply.method, apply.headers, file, opts)
    } catch (err) {
      // 上报失败但不阻断抛出
      try { await reportUploadFinish(apply.fileId, false) } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[upload] reportUploadFinish(false) failed fileId=', apply.fileId, e)
      }
      throw err
    }

    // 4) 上报成功
    await reportUploadFinish(apply.fileId, true)

    // 5) 申请检测
    await applyDetect(apply.fileId)

    // 6) 轮询检测
    const detect = await pollDetect(apply.fileId)

    // 7) 返回抽象结果
    return {
      fileId: apply.fileId,
      pages: detect.pageCount,
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      isScanned: detect.isScanned
    }
  }

  // 创建 AbortController 以支持取消上传
  const createAbortController = () => new AbortController()

  return {
    uploadFile,
    createAbortController
  }
}
